const ON_CHANGE_DEBOUNCE_TIMER = 300;

function bulkDebounce(fn, wait, immediate = false) {
  let timer;
  return (...args) => {
    const callNow = immediate && !timer;
    clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      if (!immediate) fn.apply(this, args);
    }, wait);
    if (callNow) fn.apply(this, args);
  };
}

document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-scroll-to]');
  if (!btn) return;
  document.getElementById(btn.dataset.scrollTo)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
});

const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  variantChange: 'variant-change',
  cartError: 'cart-error',
};

const _pubSubSubscribers = {};

function subscribe(eventName, callback) {
  if (!_pubSubSubscribers[eventName]) _pubSubSubscribers[eventName] = [];
  _pubSubSubscribers[eventName].push(callback);
  return function unsubscribe() {
    _pubSubSubscribers[eventName] = _pubSubSubscribers[eventName].filter((cb) => cb !== callback);
  };
}

function publish(eventName, data) {
  if (_pubSubSubscribers[eventName]) {
    _pubSubSubscribers[eventName].forEach((cb) => cb(data));
  }
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
    this._queueInterval = null;
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    if (this._queueInterval) return;
    this._queueInterval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(this._queueInterval);
        this._queueInterval = null;
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};
    queue.forEach((item) => {
      items[parseInt(item.id)] = item.quantity;
    });
    const sentIds = new Set(queue.map((item) => String(item.id)));
    this.queue = this.queue.filter((q) => !sentIds.has(String(q.id)));
    this.updateMultipleQty(items);
  }

  setRequestStarted(started) {
    this._requestStarted = started;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  getCartQuantityForLine(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    return parseInt(input?.dataset.cartQuantity || input?.getAttribute('value') || '0', 10) || 0;
  }

  /** Returns the effective max: min(ruleMax, inventory) for deny-policy variants. */
  _stockCappedMax(ruleMax, variant) {
    let max = ruleMax != null ? parseInt(ruleMax, 10) : null;
    if (variant && variant.inventory_management === 'shopify' && variant.inventory_policy === 'deny') {
      const stock = parseInt(variant.inventory_quantity, 10);
      if (!isNaN(stock) && stock > 0 && (max == null || stock < max)) max = stock;
    }
    return isNaN(max) ? null : max;
  }

  startCartLinesUpdate(items) {
    const { CartLinesUpdateEvent } = window.StandardEvents || {};
    if (!CartLinesUpdateEvent) return;

    const linesByAction = Object.entries(items).reduce((groups, [variantId, quantity]) => {
      const nextQty = parseInt(quantity, 10);
      const currentQty = this.getCartQuantityForLine(variantId);
      if (Number.isNaN(nextQty) || currentQty === nextQty) return groups;

      const action = currentQty === 0 ? 'add' : nextQty === 0 ? 'remove' : 'update';
      let line;
      if (action === 'add') {
        line = { merchandiseId: variantId, quantity: nextQty };
      } else {
        const lineKey = this.querySelector(`[data-quantity-variant-id="${variantId}"]`)?.dataset.quantityLineKey;
        if (!lineKey) return groups;
        line = { id: lineKey, quantity: nextQty };
      }
      if (!groups[action]) groups[action] = [];
      groups[action].push(line);
      return groups;
    }, {});

    const deferreds = Object.entries(linesByAction).map(([action, lines]) => {
      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'product', lines, promise: deferred.promise }));
      return deferred;
    });

    return {
      resolve: (parsedState) => {
        const payload = { cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedState) };
        deferreds.forEach((d) => d.resolve(payload));
      },
      reject: (error) => {
        deferreds.forEach((d) => d.reject(error));
      },
    };
  }

  dispatchCartErrorEvent(message, code) {
    const { CartErrorEvent } = window.StandardEvents || {};
    if (!CartErrorEvent) return;
    this.dispatchEvent(new CartErrorEvent({ error: message, code }));
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    if (!input) return;
    input.value = input.getAttribute('value');
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  async normalizeCartState(parsedState) {
    if (!parsedState || typeof parsedState.item_count === 'number') return parsedState;
    try {
      const response = await fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return parsedState;
      const fullCart = await response.json();
      return { ...parsedState, ...fullCart };
    } catch (e) {
      console.warn('[bulk-add] cart state refresh failed', e);
      return parsedState;
    }
  }
}

if (!customElements.get('quantity-popover')) {
  customElements.define(
    'quantity-popover',
    class QuantityPopover extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 990px)');
        this.mqlTablet = window.matchMedia('(min-width: 750px)');
        this.infoButtonDesktop = this.querySelector('.quantity-popover__info-button--icon-only');
        this.infoButtonMobile = this.querySelector('.quantity-popover__info-button--icon-with-label');
        this.popoverInfo = this.querySelector('.quantity-popover__info');
        this.eventMouseEnterHappened = false;

        if (this.popoverInfo) {
          this.popoverInfo.addEventListener('click', (event) => {
            if (event.target.closest('.button-close')) this.closePopover(event);
          });
        }
        if (this.popoverInfo && this.infoButtonDesktop && this.mqlTablet.matches) {
          this.popoverInfo.addEventListener('mouseleave', this.closePopover.bind(this));
        }
        if (this.infoButtonDesktop) {
          this.infoButtonDesktop.addEventListener('click', this.togglePopover.bind(this));
          this.infoButtonDesktop.addEventListener('focusout', this.closePopover.bind(this));
        }
        if (this.infoButtonMobile) {
          this.infoButtonMobile.addEventListener('click', this.togglePopover.bind(this));
        }
        if (this.infoButtonDesktop && this.mqlTablet.matches) {
          this.infoButtonDesktop.addEventListener('mouseenter', this.togglePopover.bind(this));
          this.infoButtonDesktop.addEventListener('mouseleave', this.closePopover.bind(this));
        }

        if (this.popoverInfo) {
          const hoverTarget = this.infoButtonDesktop || this.infoButtonMobile;
          if (hoverTarget) {
            const hoverEnter = (event) => {
              if (this.isInCart() && this.mql.matches && this.popoverInfo.hasAttribute('hidden')) this.togglePopover(event);
            };
            const hoverLeave = (event) => {
              if (this.isInCart() && this.mql.matches) this.closePopover(event);
            };
            hoverTarget.addEventListener('mouseenter', hoverEnter);
            hoverTarget.addEventListener('mouseleave', hoverLeave);
            this.popoverInfo.addEventListener('mouseleave', hoverLeave);
          }
        }

        if (this.querySelector('volume-pricing')) {
          this.addEventListener('change', (event) => {
            if (event.target && event.target.classList && event.target.classList.contains('quantity-input')) {
              this.updateVolumeTierHighlight();
            }
          });
        }
      }

      /** Shared PDP/cart popover: server renders the active tier once */
      updateVolumeTierHighlight() {
        if (this.closest('.card-bulk')) return;
        const pricing = this.querySelector('volume-pricing');
        const input = this.querySelector('.quantity-input');
        if (!pricing || !input) return;

        let qty = parseInt(input.value, 10);
        if (isNaN(qty)) qty = parseInt(input.dataset.min, 10) || 0;

        const lis = Array.prototype.slice.call(pricing.querySelectorAll('li'));
        let activeIndex = -1;
        lis.forEach(function (li, i) {
          const first = li.firstElementChild;
          const min = parseInt((first ? first.textContent : '').replace(/[^0-9]/g, ''), 10);
          if (!isNaN(min) && qty >= min) activeIndex = i;
        });
        lis.forEach(function (li, i) {
          li.classList.toggle('is-active', i === activeIndex);
        });
      }

      togglePopover(event) {
        event.preventDefault();
        if (event.type === 'mouseenter') this.eventMouseEnterHappened = true;
        if (event.type === 'click' && this.eventMouseEnterHappened) return;

        const button =
          this.infoButtonDesktop && this.mql.matches
            ? this.infoButtonDesktop
            : (this.infoButtonMobile ?? this.infoButtonDesktop);
        if (!button || !this.popoverInfo) return;

        const isExpanded = button.getAttribute('aria-expanded') === 'true';
        if ((this.mql.matches && !isExpanded) || event.type === 'click') {
          button.setAttribute('aria-expanded', String(!isExpanded));
          this.popoverInfo.toggleAttribute('hidden');
          button.classList.toggle('quantity-popover__info-button--open');
          this.infoButtonDesktop?.classList.add('quantity-popover__info-button--icon-only--animation');
        }

        const isOpen = button.getAttribute('aria-expanded') === 'true';
        if (isOpen && event.type !== 'mouseenter') {
          button.focus();
          button.addEventListener('keyup', (e) => {
            if (e.key === 'Escape') this.closePopover(e);
          });
        }

        if (!this.popoverInfo.hasAttribute('hidden')) {
          this.openFloatingPopover();
          this.attachOutsideClickClose();
        } else {
          this.closeFloatingPopover();
          this.detachOutsideClickClose();
        }
      }

      isInCart() {
        return this.closest('#cart') !== null;
      }

      attachOutsideClickClose() {
        if (this._outsideClickHandler || !this.isInCart()) return;
        this._outsideClickHandler = (event) => {
          if (event.target instanceof Node && this.contains(event.target)) return;
          this.forceClosePopover();
        };
        document.addEventListener('click', this._outsideClickHandler);
      }

      detachOutsideClickClose() {
        if (!this._outsideClickHandler) return;
        document.removeEventListener('click', this._outsideClickHandler);
        this._outsideClickHandler = undefined;
      }

      disconnectedCallback() {
        this.detachOutsideClickClose();
      }

      openFloatingPopover() {
        if (!this.closest('.card-bulk') || !this.popoverInfo) return;
        if (this.popoverInfo.classList.contains('card-bulk__popover--floating')) {
          this.positionPopover();
          return;
        }

        this._floatAnchor = document.createComment('quantity-popover-info-anchor');
        this.popoverInfo.parentNode.insertBefore(this._floatAnchor, this.popoverInfo);
        document.body.appendChild(this.popoverInfo);
        this.popoverInfo.classList.add('card-bulk__popover--floating');

        this.positionPopover();
        this._positionHandler = () => this.positionPopover();
        window.addEventListener('scroll', this._positionHandler, true);
        window.addEventListener('resize', this._positionHandler);
      }

      closeFloatingPopover() {
        if (!this._floatAnchor) return;
        if (this._positionHandler) {
          window.removeEventListener('scroll', this._positionHandler, true);
          window.removeEventListener('resize', this._positionHandler);
          this._positionHandler = undefined;
        }
        this.popoverInfo.classList.remove('card-bulk__popover--floating');
        ['left', 'top'].forEach((p) => {
          this.popoverInfo.style[p] = '';
        });
        this._floatAnchor.parentNode.insertBefore(this.popoverInfo, this._floatAnchor);
        this._floatAnchor.remove();
        this._floatAnchor = undefined;
      }

      positionPopover() {
        const container =
          this.querySelector('.quantity-popover-container') || this.infoButtonMobile || this.infoButtonDesktop || this;
        const rect = container.getBoundingClientRect();
        const width = this.popoverInfo.offsetWidth;
        const height = this.popoverInfo.offsetHeight;
        let left;
        let top;
        if (this.mql.matches) {
          left = rect.right - width;
          top = rect.top + 8;
        } else {
          left = rect.left;
          top = rect.bottom + 4;
        }
        left = Math.min(Math.max(left, 8), Math.max(8, window.innerWidth - width - 8));
        if (top + height > window.innerHeight - 8 && rect.top - height - 8 > 0) top = rect.top - height - 8;
        this.popoverInfo.style.left = `${left}px`;
        this.popoverInfo.style.top = `${top}px`;
      }

      forceClosePopover() {
        this.detachOutsideClickClose();
        if (!this.popoverInfo || this.popoverInfo.hasAttribute('hidden')) {
          this.closeFloatingPopover();
          return;
        }
        this.popoverInfo.setAttribute('hidden', '');
        [this.infoButtonDesktop, this.infoButtonMobile].forEach((btn) => {
          btn?.setAttribute('aria-expanded', 'false');
          btn?.classList.remove('quantity-popover__info-button--open');
        });
        this.infoButtonDesktop?.classList.remove('quantity-popover__info-button--icon-only--animation');
        this.closeFloatingPopover();
        this.eventMouseEnterHappened = false;
      }

      closePopover(event) {
        if (!event) return;
        event.preventDefault();
        const button =
          this.infoButtonDesktop && this.mql.matches
            ? this.infoButtonDesktop
            : (this.infoButtonMobile ?? this.infoButtonDesktop);
        if (!button || !this.popoverInfo) return;

        const isButtonChild =
          this.infoButtonDesktop?.contains(event.relatedTarget) || this.infoButtonMobile?.contains(event.relatedTarget);
        const isPopoverChild = this.popoverInfo.contains(event.relatedTarget);
        if (!isButtonChild && !isPopoverChild) {
          button.setAttribute('aria-expanded', 'false');
          button.classList.remove('quantity-popover__info-button--open');
          this.popoverInfo.setAttribute('hidden', '');
          this.infoButtonDesktop?.classList.remove('quantity-popover__info-button--icon-only--animation');
          this.closeFloatingPopover();
          this.detachOutsideClickClose();
        }
        this.eventMouseEnterHappened = false;
      }
    },
  );
}

// Reads volume pricing from hidden <volume-pricing id="Volume-{id}"> element.
// Updates displayed price as quantity changes or cart updates.
if (!customElements.get('price-per-item')) {
  customElements.define(
    'price-per-item',
    class PricePerItem extends HTMLElement {
      constructor() {
        super();
        this.variantId = this.dataset.variantId;
        this._scope = this.closest('quick-order-list, .card-bulk') || document;
        const qtyId = `Quantity-${this.dataset.sectionId || this.dataset.variantId}`;
        this.input =
          (this._scope !== document && this._scope.getElementById
            ? this._scope.getElementById(qtyId)
            : this._scope.querySelector(`#${qtyId}`)) || document.getElementById(qtyId);
        if (this.input) this.input.addEventListener('change', this.onInputChange.bind(this));
        this.getVolumePricingArray();
      }

      updatePricePerItemUnsubscriber = undefined;
      variantIdChangedUnsubscriber = undefined;

      connectedCallback() {
        this.variantIdChangedUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          this.variantId = event.data.variant.id.toString();
          this.getVolumePricingArray();
        });
        this.updatePricePerItemUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (response) => {
          if (!response.cartData) return;
          if (response.cartData['variant_id'] !== undefined) {
            if (response.productVariantId === this.variantId) {
              this.updatePricePerItem(response.cartData.quantity);
            }
          } else if (response.cartData.item_count !== 0) {
            const isVariant = response.cartData.items.find(
              (item) => String(item.variant_id ?? item.id ?? '') === this.variantId,
            );
            if (isVariant) {
              this.updatePricePerItem(isVariant.quantity);
            } else {
              this.updatePricePerItem(0);
            }
          } else {
            this.updatePricePerItem(0);
          }
        });
        this.getVolumePricingArray();
        this.updatePricePerItem();
      }

      disconnectedCallback() {
        this.updatePricePerItemUnsubscriber?.();
        this.variantIdChangedUnsubscriber?.();
      }

      onInputChange() {
        this.updatePricePerItem();
      }

      updatePricePerItem(updatedCartQuantity) {
        if (this.input) {
          this.enteredQty = parseInt(this.input.value);
          this.step = parseInt(this.input.step) || 1;
        }
        this.currentQtyForVolumePricing =
          updatedCartQuantity === undefined
            ? this.getCartQuantity(updatedCartQuantity) + this.enteredQty
            : this.getCartQuantity(updatedCartQuantity);

        // In the variant-item price column we show cart qty only (not entered qty)
        if (this.classList.contains('variant-item__price-per-item')) {
          this.currentQtyForVolumePricing = this.getCartQuantity(updatedCartQuantity);
        }

        if (!this.qtyPricePairs.length) return;

        const priceItem = `price-per-item[id^="Price-Per-Item-${this.dataset.sectionId || this.dataset.variantId}"] .price-per-item span`;
        const priceSpan =
          ((this._scope && this._scope !== document && this._scope.querySelector(priceItem)) ||
            document.querySelector(priceItem));
        if (!priceSpan) return;

        for (const [minQty, priceText] of this.qtyPricePairs) {
          if (this.currentQtyForVolumePricing >= minQty) {
            priceSpan.innerHTML = this.classList.contains('variant-item__price-per-item')
              ? (window.quickOrderListStrings?.each || '[money] each').replace('[money]', priceText)
              : priceText;
            break;
          }
        }
      }

      getCartQuantity(updatedCartQuantity) {
        return updatedCartQuantity || updatedCartQuantity === 0
          ? updatedCartQuantity
          : parseInt(this.input?.dataset.cartQuantity || '0');
      }

      getVolumePricingArray() {
        const volumeId = `Volume-${this.dataset.sectionId || this.dataset.variantId}`;
        const volumePricing =
          (this._scope && this._scope !== document
            ? this._scope.querySelector(`#${volumeId}`)
            : null) || document.getElementById(volumeId);
        this.qtyPricePairs = [];
        if (!volumePricing) return;
        volumePricing.querySelectorAll('li').forEach((li) => {
          const qty = parseInt(li.querySelector('span:first-child').textContent);
          const price = li.querySelector('span:not(:first-child):last-child').dataset.text;
          this.qtyPricePairs.push([qty, price]);
        });
        this.qtyPricePairs.reverse();
      }
    }
  );
}


if (!customElements.get('bulk-quantity-input')) {
  customElements.define(
    'bulk-quantity-input',
    class BulkQuantityInput extends HTMLElement {
      constructor() {
        super();
        this.input = this.querySelector('input');
        this.changeEvent = new Event('change', { bubbles: true });
        this.input.addEventListener('change', () => this.validateQtyRules(true));
        this.querySelectorAll('button').forEach((btn) =>
          btn.addEventListener('click', this.onButtonClick.bind(this))
        );
      }

      quantityUpdateUnsubscriber = undefined;

      connectedCallback() {
        this.validateQtyRules();
        this.quantityUpdateUnsubscriber = subscribe(
          PUB_SUB_EVENTS.quantityUpdate,
          () => this.validateQtyRules()
        );
      }

      disconnectedCallback() {
        this.quantityUpdateUnsubscriber?.();
      }

      getMax() {
        const htmlMax = parseInt(this.input.getAttribute('max'), 10);
        if (!isNaN(htmlMax)) return htmlMax;
        return parseInt(this.input.dataset.max, 10);
      }

      getMsg() {
        return (this.parentElement && this.parentElement.querySelector('.quantity-max-msg')) || this.querySelector('.quantity-max-msg');
      }

      showMax() {
        const msg = this.getMsg();
        if (!msg) return;
        const max = this.getMax();
        if (isNaN(max)) return;
        const source = this.input.dataset.maxSource || '';
        const tpl = msg.dataset[source === 'stock' ? 'tplStock' : 'tplRule'] || '';
        msg.textContent = tpl.replace('[quantity]', max);
        msg.hidden = false;
      }

      showMin() {
        const msg = this.getMsg();
        if (!msg) return;
        const min = parseInt(this.input.dataset.min, 10);
        const tpl = msg.dataset.tplMin || '';
        if (!tpl || isNaN(min)) return;
        msg.textContent = tpl.replace('[quantity]', min);
        msg.hidden = false;
      }

      hideMax() {
        const msg = this.getMsg();
        if (msg) msg.hidden = true;
      }

      onButtonClick(event) {
        event.preventDefault();
        const previousValue = this.input.value;
        const isPlusButton = event.currentTarget.name === 'plus';

        if (isPlusButton) {
          const minVal = parseInt(this.input.dataset.min);
          const stepVal = parseInt(this.input.step);
          if (minVal > stepVal && parseInt(this.input.value) === 0) {
            this.input.value = minVal;
          } else {
            this.input.stepUp();
          }
        } else {
          this.input.stepDown();
        }

        if (previousValue !== this.input.value) {
          this.input.dispatchEvent(this.changeEvent);
        } else if (isPlusButton) {
          const max = this.getMax();
          if (!isNaN(max) && parseInt(previousValue, 10) >= max) this.showMax();
        }

        if (this.input.dataset.min === previousValue && !isPlusButton) {
          this.input.value = parseInt(this.input.min);
        }
      }

      validateQtyRules(fromUserEdit) {
        const value = parseInt(this.input.value);
        if (this.input.min) {
          this.querySelector(".quantity__button[name='minus']")
            ?.classList.toggle('disabled', value <= parseInt(this.input.min));
        }
        const maxVal = this.getMax();
        if (!isNaN(maxVal)) {
          this.querySelector(".quantity__button[name='plus']")
            ?.classList.toggle('disabled', value >= maxVal);
        }
        const curVal = parseInt(this.input.value, 10);
        const ruleMin = parseInt(this.input.dataset.min, 10);
        if (!isNaN(maxVal) && !isNaN(curVal) && curVal > maxVal) {
          this.input.value = maxVal;
          this.showMax();
        } else if (!isNaN(ruleMin) && !isNaN(curVal) && curVal > 0 && curVal < ruleMin) {
          this.input.value = ruleMin;
          this.showMin();
        } else if (fromUserEdit) {
          this.hideMax();
        }
      }
    }
  );
}


if (!customElements.get('card-bulk-add')) {
  customElements.define(
    'card-bulk-add',
    class CardBulkAdd extends BulkAdd {
      cartUpdateUnsubscriber = undefined;

      constructor() {
        super();

        this.commitOnly = this.dataset.autoAdd === 'false';
        this._staged = null;

        const bulkDebouncedOnChange = bulkDebounce((event) => {
          const value = parseInt(event.target.value);
          if (Number.isNaN(value)) {
            this.setCartQuantity(parseInt(event.target.dataset.cartQuantity || '0', 10) || 0);
            return;
          }
          if (this.commitOnly) {
            if (value === 0) {
              this._staged = 0;
              this.updateCommitButtonState();
            } else {
              this.stageQuantity(event);
            }
            return;
          }
          if (value === 0) {
            this.startQueue(event.target.dataset.index, 0);
          } else {
            this.validateQuantity(event);
          }
        }, ON_CHANGE_DEBOUNCE_TIMER);

        this.addEventListener('change', (event) => {
          if (event.target !== this.input) return;
          bulkDebouncedOnChange(event);
        });
      }

      stageQuantity(event) {
        const target = event.target;
        const inputValue = parseInt(target.value);

        if (inputValue < parseInt(target.dataset.min)) {
          this.setValidity(event, target.dataset.index, window.quickOrderListStrings.min_error.replace('[min]', target.dataset.min));
          return;
        }
        if (target.max && inputValue > parseInt(target.max)) {
          this.setValidity(event, target.dataset.index, window.quickOrderListStrings.max_error.replace('[max]', target.max));
          return;
        }
        if (inputValue % parseInt(target.step) !== 0) {
          this.setValidity(event, target.dataset.index, window.quickOrderListStrings.step_error.replace('[step]', target.step));
          return;
        }

        target.setCustomValidity('');
        target.reportValidity();
        target.setAttribute('value', inputValue);
        this._staged = inputValue;
        this.updateCommitButtonState();
      }

      commitStaged() {
        if (!this.input) return;

        if (this._staged === null) {
          const value = parseInt(this.input.value);
          if (Number.isNaN(value)) return;
          if (value === 0) {
            this.startQueue(this.input.dataset.index, 0);
            return;
          }
          this.validateQuantity({ target: this.input });
          return;
        }

        const value = this._staged;
        this._staged = null;
        this.updateCommitButtonState();
        this.startQueue(this.input.dataset.index, value);
      }

      updateCommitButtonState() {
        if (!this.commitOnly) return;
        this.querySelectorAll('.card-bulk__add-btn').forEach((btn) =>
          btn.toggleAttribute('disabled', this._staged === null)
        );
      }

      get input() {
        return this.querySelector('input');
      }

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === this.id) return;
          if (!event.cartData || !Array.isArray(event.cartData.items)) return;
          if (document.activeElement === this.input) return;
          const variantId = parseInt(this.dataset.index, 10);
          const item = event.cartData.items.find((i) => (i.variant_id ?? i.id) === variantId);
          this.setCartQuantity(item ? item.quantity : 0);
        });

        this.input?.addEventListener('focusin', (event) => event.target.select());
        this.input?.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            if (this.commitOnly) {
              this.input.blur();
              this.commitStaged();
            } else {
              event.target.blur();
            }
          }
        });

        this.addEventListener('click', (event) => {
          const addBtn = event.target.closest('.card-bulk__add-btn');
          if (!addBtn) return;
          event.preventDefault();
          this.input?.blur();
          this.commitStaged();
        });

        this.addEventListener('click', (event) => {
          const removeBtn = event.target.closest('.card-bulk__remove');
          if (!removeBtn) return;
          this.startQueue(this.dataset.index, 0);
          if (this.input) this.input.value = 0;
        });


        this.input?.addEventListener('input', () => this.applyCardPrice());
        this.input?.addEventListener('change', () => this.applyCardPrice());
        this.initVolumePricing();
        this.updateCommitButtonState();
      }

      disconnectedCallback() {
        this.cartUpdateUnsubscriber?.();
        if (this._queueInterval) {
          clearInterval(this._queueInterval);
          this._queueInterval = null;
        }
      }

      getVolumeBreakPrice(qty) {
        let price = this._unitPrice;
        const breaks = [...(this._volumeBreaks || [])].sort(
          (a, b) => (a.minimum_quantity || 0) - (b.minimum_quantity || 0)
        );
        for (const b of breaks) {
          if ((b.minimum_quantity || 0) > 0 && qty >= b.minimum_quantity) price = b.price || price;
        }
        return price;
      }

      applyCardPrice() {
        if (typeof $s === 'undefined' || !this._unitPrice) return;
        const input = this.input;
        const min = parseInt(input?.dataset?.min, 10) || 1;
        const qty = Math.max(min, parseInt(input?.value, 10) || min);
        const price = this.getVolumeBreakPrice(qty);
        this.updateVolumeTierHighlight(qty);
        this.closest('.product-card')?.querySelectorAll('product-price').forEach((pp) => {
          const finalEl = pp.querySelector('.final');
          const compareEl = pp.querySelector('.compare');
          if (finalEl) finalEl.innerHTML = $s.formatMoney(price).replace('.00', '');
          if (compareEl && this._compareKnown) {
            const compare = this._comparePrice || 0;
            if (compare && compare > (this._unitPrice || price)) {
              compareEl.classList.remove('hide');
              compareEl.innerHTML = $s.formatMoney(compare).replace('.00', '');
            } else {
              compareEl.classList.add('hide');
              compareEl.innerHTML = '';
            }
          }
        });
      }

      updateVolumeTierHighlight(qty) {
        const popover = this.querySelector('quantity-popover');
        const scope = (popover && (popover.popoverInfo || popover)) || this;
        const pricing = scope.querySelector('volume-pricing');
        if (!pricing) return;
        let activeMin = 0;
        const breaks = [...(this._volumeBreaks || [])].sort(
          (a, b) => (a.minimum_quantity || 0) - (b.minimum_quantity || 0)
        );
        for (const b of breaks) {
          if ((b.minimum_quantity || 0) > 0 && qty >= b.minimum_quantity) activeMin = b.minimum_quantity;
        }
        pricing.querySelectorAll('li').forEach((li, i) => {
          let isActive;
          if (i === 0) {
            isActive = qty > 0 && activeMin === 0;
          } else {
            const liMin = parseInt((li.textContent.match(/\d+/) || [])[0], 10);
            isActive = liMin === activeMin;
          }
          li.classList.toggle('is-active', isActive);
        });
      }

      initVolumePricing() {
        try {
          const variantId = this.input?.dataset?.index || this.dataset.index;
          const variantDataEl = this.querySelector('.card-bulk-variant-data');
          let price = 0;
          let breaks = [];
          let compare = null;
          if (variantDataEl) {
            const vd = JSON.parse(variantDataEl.textContent)[String(variantId)];
            if (vd) {
              price = vd.price;
              breaks = (vd.breaks || []).map((b) => ({ minimum_quantity: b.qty, price: b.price }));
            }
          } else {
            const jsonEl = this.closest('[data-product-id]')?.querySelector('[data-product-json]');
            if (jsonEl) {
              const pd = JSON.parse(jsonEl.textContent);
              const v = (pd.variants || []).find((x) => x.id === parseInt(variantId, 10));
              if (v) {
                price = v.price;
                breaks = v.quantity_price_breaks || [];
                compare = v.compare_at_price;
              }
            }
          }
          if (!price) return;
          this._unitPrice = price;
          this._volumeBreaks = breaks;
          if (compare !== null && compare !== undefined) {
            this._comparePrice = compare || 0;
            this._compareKnown = true;
          }
          this.applyCardPrice();
        } catch (e) {
          // stale Liquid price is better than a JS error
        }
      }

      setRequestStarted(started) {
        super.setRequestStarted(started);
        this.toggleAttribute('loading', started);
        this.querySelector('.progress-bar-container')?.classList.toggle('hidden', !started);
        this.querySelectorAll('.quantity__button, .quantity-button').forEach((btn) =>
          btn.toggleAttribute('disabled', started)
        );
      }

      setCartQuantity(quantity) {
        const input = this.input;
        if (!input) return;
        input.value = quantity;
        input.setAttribute('value', quantity);
        input.dataset.cartQuantity = quantity;

        const removeBtn = this.querySelector('.card-bulk__remove');
        if (removeBtn) removeBtn.classList.toggle('hidden', quantity <= 0);
        const container = this.querySelector('.quantity-popover-container');
        if (container) container.classList.toggle('quantity-popover-container--empty', quantity <= 0);

        const addBtn = this.querySelector('.card-bulk__add-btn');
        if (addBtn) {
          const strings = window.quickOrderListStrings || {};
          addBtn.textContent = quantity > 0
            ? (strings.updateCart || 'Update')
            : (strings.addToCart || 'Add');
        }

        this._staged = null;
        this.updateCommitButtonState();
        this.applyCardPrice();
      }

      /** Available stock (deny policy) tightens rule.max for the input cap. */
      _stockCappedMax(ruleMax, variant) {
        let max = ruleMax != null ? parseInt(ruleMax, 10) : null;
        if (variant && variant.inventory_management === 'shopify' && variant.inventory_policy === 'deny') {
          const stock = parseInt(variant.inventory_quantity, 10);
          if (!isNaN(stock) && stock > 0 && (max == null || stock < max)) max = stock;
        }
        return isNaN(max) ? null : max;
      }

      updateVariant(variantId, available, swatchData) {
        const input = this.input;
        if (!input) return;

        const qtyWrap = this.querySelector('quantity-popover') || this.querySelector('.card-bulk__quantity-row');
        const soldOut = this.querySelector('.card-bulk__soldout');
        if (available === false) {
          if (qtyWrap) qtyWrap.style.display = 'none';
          if (soldOut) soldOut.style.display = '';
        } else {
          if (qtyWrap) qtyWrap.style.display = '';
          if (soldOut) soldOut.style.display = 'none';
        }

        if (input.dataset.index === String(variantId)) return;

        let pjVariant = null;
        try {
          const jsonEl0 = this.closest('[data-product-id]')?.querySelector('[data-product-json]');
          if (jsonEl0) {
            pjVariant = (JSON.parse(jsonEl0.textContent).variants || []).find((v) => v.id === parseInt(variantId, 10)) || null;
          }
        } catch (e) {
        }

        let sw = null;
        try {
          if (swatchData && swatchData.varMin !== undefined) {
            const qtyList = `${swatchData.varBreakQty || ''}`.split(',').filter((s) => s !== '');
            const priceList = `${swatchData.varBreakPrice || ''}`.split(',').filter((s) => s !== '');
            sw = {
              min: parseInt(swatchData.varMin, 10) || 1,
              increment: parseInt(swatchData.varIncrement, 10) || 1,
              max: swatchData.varMax ? parseInt(swatchData.varMax, 10) : null,
              price: parseInt(swatchData.varPrice, 10) || 0,
              compare: swatchData.varCompare ? parseInt(swatchData.varCompare, 10) : 0,
              breaks: qtyList.map((qty, i) => ({
                minimum_quantity: parseInt(qty, 10) || 0,
                price: parseInt(priceList[i], 10) || 0
              }))
            };
          }
        } catch (e) {
          sw = null;
        }

        if (sw && sw.price) {
          this._unitPrice = sw.price;
          this._comparePrice = sw.compare || 0;
          this._compareKnown = true;
          this._volumeBreaks = sw.breaks || [];
          this.applyCardPrice();
        }

        this.dataset.index = variantId;
        input.dataset.index = variantId;
        input.dataset.quantityVariantId = variantId;
        input.name = `updates[${variantId}]`;
        input.id = `Quantity-Card-${variantId}-${this.dataset.cardKey}`;
        this.querySelector('label[for]')?.setAttribute('for', input.id);
        if (this.id) this.id = `quick-add-bulk-${variantId}-${this.dataset.cardKey}`;

        try {
          if (sw) {
            input.dataset.min = sw.min;
            input.step = sw.increment;
            const effMax = this._stockCappedMax(sw.max, pjVariant);
            if (effMax != null) {
              input.max = effMax;
            } else {
              input.removeAttribute('max');
            }
          }
        } catch (e) {
          // ignore
        }
        try {
          if (!sw && pjVariant) {
            const rule = (pjVariant && pjVariant.quantity_rule) || {};
            input.dataset.min = rule.min ?? 1;
            input.step = rule.increment ?? 1;
            const effMax = this._stockCappedMax(rule.max, pjVariant);
            if (effMax != null) {
              input.max = effMax;
            } else {
              input.removeAttribute('max');
            }
          }
        } catch (e) {
        }

        this.querySelector('bulk-quantity-input')?.validateQtyRules?.();

        try {
          this.querySelector('quantity-popover')?.forceClosePopover?.();

          const variantDataEl = this.querySelector('.card-bulk-variant-data');
          let rule = {};
          let priceBreaks = [];
          let variantPrice = 0;

          if (sw) {
            rule = { min: sw.min, increment: sw.increment, max: sw.max };
            priceBreaks = sw.breaks;
            variantPrice = sw.price;
          } else if (variantDataEl) {
            const variantMap = JSON.parse(variantDataEl.textContent);
            const vd = variantMap[String(variantId)];
            if (vd) {
              priceBreaks = (vd.breaks || []).map((b) => ({ minimum_quantity: b.qty, price: b.price }));
              variantPrice = vd.price;
              rule = { min: vd.min };

              const jsonEl2 = this.closest('[data-product-id]')?.querySelector('[data-product-json]');
              if (jsonEl2) {
                const pd = JSON.parse(jsonEl2.textContent);
                const vj = (pd.variants || []).find((vv) => vv.id === parseInt(variantId, 10));
                if (vj?.quantity_rule) Object.assign(rule, vj.quantity_rule);
              }
            }
          } else {
            const jsonEl2 = this.closest('[data-product-id]')?.querySelector('[data-product-json]');
            if (jsonEl2) {
              const pd = JSON.parse(jsonEl2.textContent);
              const v = (pd.variants || []).find((vv) => vv.id === parseInt(variantId, 10));
              if (v) {
                rule = v.quantity_rule || {};
                priceBreaks = v.quantity_price_breaks || [];
                variantPrice = v.price;
              }
            }
          }

          const hasQtyRules = (rule.increment > 1) || (rule.min > 1) || (rule.max != null);
          const hasVolPricing = priceBreaks.length > 0;
          const hasPopover = hasQtyRules || hasVolPricing;

          if (variantPrice) {
            this._unitPrice = variantPrice;
            this._volumeBreaks = priceBreaks;
            this.applyCardPrice();
          }

          const popoverBtns = this.querySelectorAll('.quantity-popover__info-button');
          const popoverInfo = this.querySelector('.quantity-popover__info');
          const container = this.querySelector('.quantity-popover-container');
          const wrapper = this.querySelector('.variant-item__quantity-wrapper');

          const infoLabel = hasVolPricing ? 'View pricing information' : 'View quantity information';
          popoverBtns.forEach((btn) => {
            btn.style.display = hasPopover ? '' : 'none';
            btn.setAttribute('aria-label', infoLabel);
            const labelSpan = btn.querySelector('span:not(.visually-hidden)');
            if (labelSpan) labelSpan.textContent = infoLabel;
          });

          if (wrapper) wrapper.classList.toggle('variant-item__quantity-wrapper--no-info', !hasPopover);
          if (container) container.classList.toggle('quantity-popover-container--hover', hasPopover && available !== false);

          if (popoverInfo) {
            const safeVariantId = parseInt(variantId, 10);
            const safeCardKey = String(this.dataset.cardKey || '').replace(/[^a-z0-9_-]/gi, '');
            const parts = [];

            if (!hasQtyRules && hasVolPricing) {
              parts.push('<span class="volume-pricing-label caption">Volume pricing</span>');
            }

            const ruleItems = [];
            if (rule.increment > 1) ruleItems.push(`<span class="divider">Multiples of ${rule.increment}</span>`);
            if (rule.min > 1) ruleItems.push(`<span class="divider">Minimum of ${rule.min}</span>`);
            const maxForDisplay = this._stockCappedMax(rule.max, pjVariant);
            if (maxForDisplay != null) ruleItems.push(`<span class="divider">Maximum of ${maxForDisplay}</span>`);
            parts.push(`<div class="quantity__rules caption no-js-hidden">${ruleItems.join('')}</div>`);

            parts.push('<button class="button-close button button--tertiary" type="button" aria-label="Close"><svg aria-hidden="true" focusable="false" class="icon icon-close" viewBox="0 0 32 32"><path d="M4.2,4.2,27.8,27.8m-23.6,0L27.8,4.2"/></svg></button>');

            if (hasVolPricing) {
              const currentQty = parseInt(this.input?.dataset.cartQuantity || '0', 10);
              let activeBreakMin = 0;
              for (const pb of priceBreaks) {
                if (currentQty >= pb.minimum_quantity) activeBreakMin = pb.minimum_quantity;
              }
              const formatMoney = (cents) => {
                if (window.Shopify?.formatMoney && window.Shopify?.money_format_with_currency) {
                  return Shopify.formatMoney(cents, Shopify.money_format_with_currency);
                }
                const sym = (window.Shopify?.moneyFormat || '${{amount}}').replace(/\{\{.*?\}\}/, '');
                return `${sym}${(cents / 100).toFixed(2)}`;
              };
              const liItems = [];
              const baseActive = (currentQty > 0 && activeBreakMin === 0) ? ' class="is-active"' : '';
              liItems.push(`<li${baseActive}><span>${rule.min || 1}+</span><span>${formatMoney(variantPrice)}<span class="price-unit">/ea</span></span></li>`);
              for (const pb of priceBreaks) {
                const pbActive = activeBreakMin === pb.minimum_quantity ? ' class="is-active"' : '';
                liItems.push(`<li${pbActive}><span>${pb.minimum_quantity}<span aria-hidden="true">+</span></span><span>${formatMoney(pb.price)}<span class="price-unit">/ea</span></span></li>`);
              }
              parts.push(`<volume-pricing id="volume-popover-card-${safeVariantId}-${safeCardKey}" class="parent-display"><ul class="list-unstyled">${liItems.join('')}</ul></volume-pricing>`);
            }

            popoverInfo.innerHTML = parts.join('');
          }
        } catch (e) {
          console.warn('[bulk-add] updateVariant popover render failed', e);
        }

        const icon = this.querySelector('.card-bulk__message-icon');
        const text = this.querySelector('.card-bulk__message-text');
        if (icon) icon.classList.add('hidden');
        if (text) text.innerHTML = '';
        clearTimeout(this._addedTimer);
        this.removeAttribute('data-msg-added');
        this.removeAttribute('data-msg-removed');

        this.setCartQuantity(0);
        if (available !== false) this.refreshCartQuantity();
      }

      refreshCartQuantity() {
        fetch(`${window.routes.cart_url}.js`)
          .then((response) => response.json())
          .then((cart) => {
            const item = (cart.items || []).find((i) => i.id === parseInt(this.dataset.index, 10));
            this.setCartQuantity(item ? item.quantity : 0);
          })
          .catch((e) => { console.warn('[bulk-add] refreshCartQuantity failed', e); });
      }

      updateMultipleQty(items) {
        const ids = Object.keys(items);
        if (ids.length === 0) {
          this.setRequestStarted(false);
          return;
        }
        const variantId = parseInt(ids[0], 10);
        const newQty = parseInt(items[variantId] ?? items[ids[0]], 10);
        const isCurrentVariant = variantId === parseInt(this.dataset.index, 10);
        const prevQty = isCurrentVariant ? parseInt(this.input?.dataset.cartQuantity || '0', 10) || 0 : 0;

        let url = `${window.routes.cart_update_url}`;
        let body;
        if (prevQty === 0 && newQty > 0) {
          url = `${window.routes.cart_add_url}`;
          body = JSON.stringify({
            items: [{ id: variantId, quantity: newQty, properties: { _added_through_site: 'true' } }],
          });
        } else {
          body = JSON.stringify({ updates: items });
        }

        fetch(url, { ...fetchConfig(), ...{ body } })
          .then(async (response) => {
            let parsedState = await response.json();
            if (!response.ok || parsedState.errors || (parsedState.status && (parsedState.message || parsedState.description))) {
              throw Object.assign(
                new Error(parsedState.description || parsedState.message || window.cartStrings.error),
                { code: 'INVALID' },
              );
            }
            parsedState = await this.normalizeCartState(parsedState);
            this.updateCartCount(parsedState.item_count);
            publish(PUB_SUB_EVENTS.cartUpdate, { source: this.id, cartData: parsedState });
            if (!isCurrentVariant) return;
            this.setCartQuantity(newQty);
            if (newQty > prevQty) {
              this.showAddedMessage(newQty - prevQty);
              if (window.__essentialUpsellForceToastOnly) window.$s?.addToCartSuccess?.({ showToastOnly: true });
            } else if (newQty < prevQty) {
              this.showRemovedMessage(prevQty - newQty);
            }
          })
          .catch((e) => {
            if (e.code !== 'INVALID') console.error(e);
            const message = e.code === 'INVALID' ? e.message : window.cartStrings.error;
            this.dispatchCartErrorEvent(message, e.code || 'SERVICE_UNAVAILABLE');
            if (isCurrentVariant) this.setCartQuantity(prevQty);
            window.$s?.addToCartFail(message);
          })
          .finally(() => {
            this.setRequestStarted(false);
          });
      }

      showAddedMessage(delta) {
        const icon = this.querySelector('.card-bulk__message-icon');
        const text = this.querySelector('.card-bulk__message-text');
        if (!icon || !text) return;

        const strings = window.quickOrderListStrings || {};
        const tpl = delta === 1 ? strings.itemAdded : strings.itemsAdded;
        text.innerHTML = tpl ? tpl.replace('[quantity]', delta) : `${delta} added`;
        if (icon) icon.classList.remove('hidden');
        this.removeAttribute('data-msg-removed');
        this.setAttribute('data-msg-added', '');

        clearTimeout(this._addedTimer);
        this._addedTimer = setTimeout(() => {
          text.innerHTML = '';
          icon.classList.add('hidden');
          this.removeAttribute('data-msg-added');
        }, 3500);
      }

      showRemovedMessage(delta) {
        const icon = this.querySelector('.card-bulk__message-icon');
        const text = this.querySelector('.card-bulk__message-text');
        if (!text) return;

        const strings = window.quickOrderListStrings || {};
        const tpl = delta === 1 ? strings.itemRemoved : strings.itemsRemoved;
        text.innerHTML = tpl ? tpl.replace('[quantity]', delta) : `${delta} removed`;
        if (icon) icon.classList.remove('hidden');
        this.removeAttribute('data-msg-added');
        this.setAttribute('data-msg-removed', '');

        clearTimeout(this._addedTimer);
        this._addedTimer = setTimeout(() => {
          text.innerHTML = '';
          if (icon) icon.classList.add('hidden');
          this.removeAttribute('data-msg-removed');
        }, 3500);
      }

      updateCartCount(itemCount) {
        if (typeof itemCount === 'undefined') return;
        document.querySelectorAll('.cart-count').forEach((counter) => {
          if (counter.classList.contains('badge')) {
            counter.textContent = itemCount > 0 ? itemCount : '';
          } else {
            counter.textContent = itemCount > 0 ? `(${itemCount})` : '';
          }
          counter.closest('a.trigger-cart')?.classList.toggle('has-items', itemCount > 0);
        });
      }
    }
  );
}


if (!customElements.get('bulk-modal')) {
  customElements.define(
    'bulk-modal',
    class BulkModal extends HTMLElement {
      connectedCallback() {
        this._onDocClick = (event) => {
          const trigger = event.target.closest('.card-bulk-trigger');
          if (!trigger) return;
          const card = trigger.closest('product-card-atc, .product-card');
          if (!card || card.querySelector('.card-bulk bulk-modal') !== this) return;
          event.preventDefault();
          this.open();
        };
        document.addEventListener('click', this._onDocClick);

        const quickBulkTrigger = document.querySelector(
          `#QuickBulk-${this.dataset.productId}-${this.dataset.cardKey}`
        );
        if (quickBulkTrigger) {
          const observer = new IntersectionObserver((entries, obs) => {
            if (!entries[0].isIntersecting) return;
            obs.unobserve(quickBulkTrigger);
            this.loadContents().catch((e) => { console.warn('[bulk-add] lazy load failed', e); });
          });
          observer.observe(quickBulkTrigger);
        }
      }

      disconnectedCallback() {
        document.removeEventListener('click', this._onDocClick);
      }

      loadContents() {
        if (this._templateHtml) return Promise.resolve();
        if (this._loadPromise) return this._loadPromise;

        const productUrl = this.dataset.url.split('?')[0];
        this._loadPromise = fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => {
            if (!response.ok) throw new Error(`Quick order list fetch failed (${response.status})`);
            return response.text();
          })
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const source = html.querySelector('.quick-order-list__section-inner');
            if (!source) throw new Error('Quick order list section not found');
            this._templateHtml = source.outerHTML;
            this._loadPromise = null;
          })
          .catch((e) => {
            console.error(e);
            this._loadPromise = null;
            throw e;
          });

        return this._loadPromise;
      }

      open() {
        if (this._opening) return;

        if (this._templateHtml) {
          this.showModal();
          return;
        }

        this._opening = true;
        window.$s?.showLoadingOverlay();
        this.loadContents()
          .catch((e) => { console.warn('[bulk-add] modal load failed', e); })
          .finally(() => {
            window.$s?.hideLoadingOverlay();
            this._opening = false;
            this.showModal();
          });
      }

      showModal() {
        if (this._opening) return;

        if (!this._templateHtml) {
          window.$s?.addToCartFail(window.cartStrings?.error || 'Something went wrong. Please try again.');
          return;
        }

        this._opening = true;

        const title   = this.dataset.title || '';
        const imgSrc  = this.dataset.image || '';
        const imgAlt  = this.dataset.imageAlt || title;
        const pageUrl = this.dataset.url || '';
        const safeTitle = title.replace(/</g, '&lt;');
        const safeAlt   = imgAlt.replace(/"/g, '&quot;');
        const viewDetailsLabel = window.quickOrderListStrings?.viewDetails || 'View full details';
        const closeSvg = `<svg aria-hidden="true" class="icon icon-close" viewBox="0 0 32 32"><path d="M4.2,4.2,27.8,27.8m-23.6,0L27.8,4.2" stroke="currentColor" stroke-width="2" fill="none"/></svg>`;

        const mediaCol = imgSrc ? `
          <div class="card-bulk-modal__media">
            <div class="card-bulk-modal__media-inner">
              <div class="card-bulk-modal__media-img">
                <img src="${imgSrc}" alt="${safeAlt}" loading="lazy" width="360">
              </div>
              <a href="${pageUrl}" class="card-bulk-modal__view-details cbm-desktop-only">
                ${viewDetailsLabel} &rarr;
              </a>
            </div>
            <div class="card-bulk-modal__media-info cbm-mobile-only">
              <h2 class="card-bulk-modal__title">${safeTitle}</h2>
              <button type="button" class="card-bulk-modal__close-btn" aria-label="Close dialog">
                ${closeSvg}
              </button>
            </div>
          </div>` : '';


        const content = document.createElement('div');
        content.className = 'card-bulk-modal-content';
        content.innerHTML = `
          <div class="card-bulk-modal__body">
            ${mediaCol}
            <div class="card-bulk-modal__list">
              <div class="card-bulk-modal__list-header${imgSrc ? ' cbm-desktop-only' : ''}">
                <h2 class="card-bulk-modal__title">${safeTitle}</h2>
                <button type="button" class="card-bulk-modal__close-btn" aria-label="Close dialog">
                  ${closeSvg}
                </button>
              </div>
              ${this._templateHtml}
            </div>
          </div>`;

        const injectedList = content.querySelector('quick-order-list');
        if (injectedList) injectedList.id = `quick-order-list-card-${this.dataset.cardKey}`;

        const modalInstance = window.$s.openModal(content, 'card-bulk-modal', {
          close: () => {
            this._opening = false;
            document
              .querySelectorAll('.card-bulk-modal-content quick-order-list')
              .forEach((list) => list.remove());
          },
        });

        content.querySelectorAll('.card-bulk-modal__close-btn').forEach((btn) => {
          btn.addEventListener('click', () => modalInstance?.close?.());
        });
      }
    }
  );
}
